<template>
  <div>
    <basic-container>
      <div class="roster-container">
        <!-- 搜索条件区域 -->
        <div class="search-container">
          <el-form :inline="true" class="demo-form-inline" @submit.native.prevent>
            <el-form-item label="关键字：">
              <el-input v-model="searchForm.keyword" placeholder="姓名/手机号" clearable
                @keyup.enter.native="handleSearch" style="width: 180px" />
            </el-form-item>
            <el-form-item label="分组：">
              <el-select v-model="searchForm.groupId" placeholder="全部分组" clearable filterable
                style="width: 170px">
                <el-option v-for="g in groupOptions" :key="g.id" :label="g.groupName" :value="g.id" />
              </el-select>
            </el-form-item>
            <el-form-item label="服务区域：">
              <el-cascader v-model="searchAreaPath" :options="areaCascaderOptions" :props="areaCascaderProps"
                placeholder="省 / 市 / 区" clearable style="width: 240px"
                @active-item-change="loadAreaCascaderChildren" />
            </el-form-item>
            <el-form-item>
              <el-button type="primary" icon="el-icon-search" size="small" @click="handleSearch">搜索</el-button>
              <el-button icon="el-icon-refresh" size="small" @click="resetSearch">重置</el-button>
            </el-form-item>
          </el-form>
        </div>

        <!-- 花名册列表 -->
        <el-table :data="tableData" v-loading="tableLoading" border stripe style="width: 100%">
          <el-table-column type="index" label="序号" width="60" align="center" />
          <el-table-column prop="engineerName" label="姓名" min-width="90" align="center" />
          <el-table-column prop="engineerMobile" label="手机号" min-width="120" align="center" />
          <el-table-column prop="groupName" label="所属分组" min-width="120" align="center">
            <template slot-scope="scope">
              <span>{{ scope.row.groupName || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="接单区域" min-width="200" align="center">
            <template slot-scope="scope">
              <el-tooltip v-if="(scope.row.serviceAreas || []).length > 1" placement="top">
                <div slot="content">
                  <div v-for="(a, i) in scope.row.serviceAreas" :key="a.id || i">
                    {{ formatAreaText(a) }}
                  </div>
                </div>
                <span>
                  {{ scope.row.serviceAreaText || '-' }}
                  <el-tag size="mini" type="info" effect="plain">共{{ scope.row.serviceAreas.length }}个</el-tag>
                </span>
              </el-tooltip>
              <span v-else>{{ scope.row.serviceAreaText || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="今日排班" width="90" align="center">
            <template slot-scope="scope">
              <el-tag size="mini" :type="scope.row.todayScheduleStatus === '休假' ? 'info' : 'success'" effect="plain">
                {{ scope.row.todayScheduleStatus || '-' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="今日考勤" width="90" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.todayAttendanceStatus" size="mini" effect="plain"
                :type="scope.row.todayAttendanceStatus === '正常' ? 'success' : scope.row.todayAttendanceStatus === '迟到' ? 'warning' : 'danger'">
                {{ scope.row.todayAttendanceStatus }}
              </el-tag>
              <span v-else style="color:#c0c4cc">-</span>
            </template>
          </el-table-column>
          <el-table-column label="今日施工状态" width="110" align="center">
            <template slot-scope="scope">
              <el-tag size="mini" effect="plain" :type="constructionTagType(scope.row.todayConstructionStatus)">
                {{ scope.row.todayConstructionStatus || '-' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="今日工单" width="90" align="center">
            <template slot-scope="scope">
              <span>{{ scope.row.todayOrderCount || 0 }}</span>
              <el-tag v-if="scope.row.todayOrderCount >= 2" size="mini" type="danger" effect="plain"
                style="margin-left:4px">今日多单</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="weekOrderCount" label="本周工单" width="80" align="center" />
          <el-table-column label="在途工单" width="100" align="center">
            <template slot-scope="scope">
              <span>{{ scope.row.inServiceOrderCount || 0 }}</span>
              <el-tooltip v-if="isOverdueInService(scope.row)" placement="top"
                content="已接单但今日/本周 0 单，存在超期在途工单">
                <i class="el-icon-warning" style="color:#e6a23c;margin-left:4px"></i>
              </el-tooltip>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="160" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button type="text" icon="el-icon-edit" size="small" @click="openEditDialog(scope.row)">编辑</el-button>
              <el-button type="text" icon="el-icon-date" size="small" @click="openWeekBoard(scope.row)">周看板</el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-container">
          <el-pagination background layout="total, sizes, prev, pager, next, jumper"
            :current-page="page.currentPage" :page-size="page.pageSize" :page-sizes="[10, 20, 50, 100]"
            :total="page.total" @current-change="pageChange" @size-change="pageSizeChange" />
        </div>
      </div>

      <!-- 编辑弹窗：修改分组 / 接单区域 -->
      <el-dialog title="编辑工程师" :visible.sync="editDialogVisible" width="720px"
        :close-on-click-modal="false" @close="resetEditForm">
        <el-form ref="editForm" :model="editForm" label-width="90px" size="small">
          <el-row :gutter="16">
            <el-col :span="12">
              <el-form-item label="姓名">
                <el-input :value="editForm.engineerName" disabled />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="手机号">
                <el-input :value="editForm.engineerMobile" disabled />
              </el-form-item>
            </el-col>
          </el-row>
          <el-form-item label="所属分组" prop="groupId"
            :rules="[{ required: true, message: '请选择分组', trigger: 'change' }]">
            <el-select v-model="editForm.groupId" placeholder="请选择分组" filterable style="width: 100%">
              <el-option v-for="g in groupOptions" :key="g.id" :label="g.groupName" :value="g.id" />
            </el-select>
          </el-form-item>
          <el-form-item label="接单区域">
            <div class="area-row" v-for="(item, index) in editForm.areas" :key="index">
              <el-select v-model="item.provinceObj" placeholder="省" filterable value-key="id"
                style="width: 170px" @change="onEditProvinceChange(item)">
                <el-option v-for="p in provinceOptions" :key="p.id" :label="p.name" :value="p" />
              </el-select>
              <el-select v-model="item.cityObj" placeholder="市" filterable value-key="id"
                style="width: 170px; margin-left: 8px" @change="onEditCityChange(item)">
                <el-option v-for="c in item.cityOptions" :key="c.id" :label="c.name" :value="c" />
              </el-select>
              <el-select v-model="item.areaObj" placeholder="区" filterable value-key="id"
                style="width: 170px; margin-left: 8px">
                <el-option v-for="a in item.areaOptions" :key="a.id" :label="a.name" :value="a" />
              </el-select>
              <el-button type="text" icon="el-icon-delete" style="color:#f56c6c;margin-left:8px"
                @click="removeAreaRow(index)">删除</el-button>
            </div>
            <el-button type="text" icon="el-icon-plus" @click="addAreaRow">添加区域</el-button>
            <div class="form-tip">
              <i class="el-icon-info"></i>
              保存时全量提交接单区域，删除全部区域即清空该工程师接单区域
            </div>
          </el-form-item>
        </el-form>
        <div slot="footer" class="dialog-footer">
          <el-button @click="editDialogVisible = false">取 消</el-button>
          <el-button type="primary" :loading="submitLoading" @click="submitEdit">保 存</el-button>
        </div>
      </el-dialog>

      <!-- 工程师个人周看板弹窗 -->
      <engineer-week-board ref="weekBoard"></engineer-week-board>
    </basic-container>
  </div>
</template>

<script>
import {
  getRosterList,
  updateRoster,
  findProvince,
  findCitys,
  findAreas,
} from "@/api/sales/engineerRoster";
import { findUser } from "@/api/sales/accountGroup";
import engineerWeekBoard from "@/views/work/waterProof/merchants/components/engineerWeekBoard.vue";

export default {
  name: "engineerRoster",
  components: { engineerWeekBoard },
  data() {
    return {
      // 搜索条件
      searchForm: {
        keyword: "",
        groupId: "",
      },
      // 搜索区省市区级联（选中值为 [省id, 市id, 区id]）
      searchAreaPath: [],
      areaCascaderOptions: [],
      areaCascaderProps: { value: "id", label: "name", children: "children" },
      // 省字典（编辑弹窗共用）
      provinceOptions: [],
      // 分组下拉
      groupOptions: [],
      // 列表
      tableData: [],
      tableLoading: false,
      page: { total: 0, currentPage: 1, pageSize: 20 },
      // 编辑弹窗
      editDialogVisible: false,
      submitLoading: false,
      editForm: {
        userId: "",
        engineerName: "",
        engineerMobile: "",
        groupId: "",
        areas: [],
      },
    };
  },
  created() {
    this.loadGroupOptions();
    this.loadProvinceOptions();
    this.handleList();
  },
  methods: {
    /* ====== 基础数据 ====== */
    loadGroupOptions() {
      // 分组下拉复用现有分组管理接口，服务端按登录人机构隔离，一次拉全
      findUser({ page: 0, size: 500 })
        .then((res) => {
          const data = this.parseData(res);
          this.groupOptions = (data && data.rows) || [];
        })
        .catch((err) => {
          console.log("loadGroupOptions error:", err);
        });
    },
    loadProvinceOptions() {
      findProvince()
        .then((res) => {
          this.provinceOptions = this.parseData(res) || [];
          // 级联搜索选项：children 置空数组用于动态加载下级
          this.areaCascaderOptions = this.provinceOptions.map((p) => ({
            id: p.id,
            name: p.name,
            children: [],
          }));
        })
        .catch((err) => {
          console.log("loadProvinceOptions error:", err);
        });
    },
    // 兼容 res.data 为 JSON 字符串或对象两种返回
    parseData(res) {
      if (!res) return null;
      const raw = res.data !== undefined ? res.data : res;
      if (typeof raw === "string") {
        try {
          return JSON.parse(raw);
        } catch (e) {
          return null;
        }
      }
      return raw;
    },
    /* ====== 搜索区省市区级联（动态加载下级） ====== */
    loadAreaCascaderChildren(path) {
      if (!path || !path.length) return;
      const province = this.areaCascaderOptions.find((p) => p.id === path[0]);
      if (!province) return;
      if (path.length === 1) {
        if (province.children.length) return;
        findCitys(path[0]).then((res) => {
          const cities = this.parseData(res) || [];
          province.children = cities.map((c) => ({
            id: c.id,
            name: c.name,
            children: [],
          }));
        });
      } else if (path.length === 2) {
        const city = province.children.find((c) => c.id === path[1]);
        if (!city || city.children.length) return;
        findAreas(path[1]).then((res) => {
          const areas = this.parseData(res) || [];
          // 区为叶子节点，不带 children
          city.children = areas.map((a) => ({ id: a.id, name: a.name }));
        });
      }
    },
    /* ====== 列表 ====== */
    handleSearch() {
      this.page.currentPage = 1;
      this.handleList();
    },
    resetSearch() {
      this.searchForm = { keyword: "", groupId: "" };
      this.searchAreaPath = [];
      this.page.currentPage = 1;
      this.handleList();
    },
    handleList() {
      this.tableLoading = true;
      const params = {
        page: this.page.currentPage,
        pageSize: this.page.pageSize,
      };
      if (this.searchForm.keyword) params.keyword = this.searchForm.keyword;
      if (this.searchForm.groupId) params.groupId = this.searchForm.groupId;
      // 服务区域按区级国标码硬过滤（级联选到区级才生效）
      if (this.searchAreaPath.length === 3) params.serviceAreaCode = this.searchAreaPath[2];
      getRosterList(params)
        .then((res) => {
          if (res.code && res.code !== "200") {
            this.tableData = [];
            this.page.total = 0;
            this.$message.error(res.message || "获取花名册列表失败");
            return;
          }
          const data = this.parseData(res) || {};
          this.tableData = data.rows || [];
          this.page.total = data.total || 0;
        })
        .catch((err) => {
          console.log("handleList error:", err);
          this.tableData = [];
          this.page.total = 0;
          this.$message.error("获取花名册列表异常");
        })
        .finally(() => {
          this.tableLoading = false;
        });
    },
    pageChange(page) {
      this.page.currentPage = page;
      this.handleList();
    },
    pageSizeChange(pageSize) {
      this.page.pageSize = pageSize;
      this.page.currentPage = 1;
      this.handleList();
    },
    /* ====== 列表展示辅助 ====== */
    formatAreaText(a) {
      if (!a) return "-";
      return `${a.provinceName || ""}${a.cityName || ""}${a.areaName || ""}` || "-";
    },
    constructionTagType(status) {
      const map = {
        待单中: "info",
        已接单: "primary",
        施工中: "warning",
        休假中: "info",
      };
      return map[status] || "info";
    },
    // 已接单但今日/本周 0 单时，据在途单数提示存在超期在途单
    isOverdueInService(row) {
      return (
        row.todayConstructionStatus === "已接单" &&
        !(row.todayOrderCount > 0) &&
        !(row.weekOrderCount > 0) &&
        row.inServiceOrderCount > 0
      );
    },
    // 查看工程师个人周看板
    openWeekBoard(row) {
      this.$refs.weekBoard.open({
        engineerUserId: row.userId,
        engineerName: row.engineerName,
      });
    },
    /* ====== 编辑弹窗 ====== */
    buildAreaRow(a = {}) {
      return {
        provinceObj: a.province ? { id: a.province, name: a.provinceName || "" } : "",
        cityObj: a.city ? { id: a.city, name: a.cityName || "" } : "",
        areaObj: a.area ? { id: a.area, name: a.areaName || "" } : "",
        cityOptions: [],
        areaOptions: [],
      };
    },
    openEditDialog(row) {
      this.editForm = {
        userId: row.userId,
        engineerName: row.engineerName || "",
        engineerMobile: row.engineerMobile || "",
        groupId: row.groupId || "",
        areas: (row.serviceAreas || []).map((a) => this.buildAreaRow(a)),
      };
      // 回显各行的市/区下拉选项
      this.editForm.areas.forEach((item) => {
        if (item.provinceObj && item.provinceObj.id) {
          findCitys(item.provinceObj.id).then((res) => {
            item.cityOptions = this.parseData(res) || [];
          });
        }
        if (item.cityObj && item.cityObj.id) {
          findAreas(item.cityObj.id).then((res) => {
            item.areaOptions = this.parseData(res) || [];
          });
        }
      });
      this.editDialogVisible = true;
      this.$nextTick(() => {
        if (this.$refs.editForm) this.$refs.editForm.clearValidate();
      });
    },
    resetEditForm() {
      this.editForm = {
        userId: "",
        engineerName: "",
        engineerMobile: "",
        groupId: "",
        areas: [],
      };
    },
    addAreaRow() {
      this.editForm.areas.push(this.buildAreaRow());
    },
    removeAreaRow(index) {
      this.editForm.areas.splice(index, 1);
    },
    onEditProvinceChange(item) {
      item.cityObj = "";
      item.areaObj = "";
      item.cityOptions = [];
      item.areaOptions = [];
      if (!item.provinceObj || !item.provinceObj.id) return;
      findCitys(item.provinceObj.id).then((res) => {
        item.cityOptions = this.parseData(res) || [];
      });
    },
    onEditCityChange(item) {
      item.areaObj = "";
      item.areaOptions = [];
      if (!item.cityObj || !item.cityObj.id) return;
      findAreas(item.cityObj.id).then((res) => {
        item.areaOptions = this.parseData(res) || [];
      });
    },
    submitEdit() {
      this.$refs.editForm.validate((valid) => {
        if (!valid) return;
        // 校验区域行：区编码不能为空
        for (let i = 0; i < this.editForm.areas.length; i++) {
          const item = this.editForm.areas[i];
          if (!item.areaObj || !item.areaObj.id) {
            this.$message.warning(`第 ${i + 1} 个接单区域请选择到区/县`);
            return;
          }
        }
        // 全量提交：编码与名称成对传给服务端（服务端用名称做正向地理编码）
        const serviceAreas = this.editForm.areas.map((item) => ({
          province: String(item.provinceObj.id),
          provinceName: item.provinceObj.name,
          city: String(item.cityObj.id),
          cityName: item.cityObj.name,
          area: String(item.areaObj.id),
          areaName: item.areaObj.name,
        }));
        const data = {
          userId: this.editForm.userId,
          groupId: this.editForm.groupId,
          serviceAreas: serviceAreas,
        };
        this.submitLoading = true;
        updateRoster(data)
          .then((res) => {
            // 成功 HTTP 200 无响应体；有 code 时按业务码判断
            if (res && res.code && res.code !== "200") {
              this.$message.error(res.message || "保存失败");
              return;
            }
            this.$message.success("保存成功");
            this.editDialogVisible = false;
            this.handleList();
          })
          .catch((err) => {
            console.log("submitEdit error:", err);
            const msg =
              err && err.response && err.response.data && err.response.data.message;
            this.$message.error(msg || "保存异常");
          })
          .finally(() => {
            this.submitLoading = false;
          });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.roster-container {
  .search-container {
    margin-bottom: 8px;
  }

  .pagination-container {
    margin-top: 16px;
    text-align: right;
  }
}

.area-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.form-tip {
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
  margin-top: 4px;

  i {
    margin-right: 4px;
  }
}
</style>
